import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomModal,
  CustomSelect,
  CustomTextInput,
  Option,
  successDialog,
  Text,
  useText,
} from '@src/components';
import { Form } from 'antd';
import modalClose from '@src/assets/icons/icon-close.svg';
import '@src/styles/cardOrder/deliveryAddressAddForm.scss';
import { formPhoneRegex } from '@src/utils/formRule';
import { turkishToLower } from '@src/utils/utils';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cityGetAll, countyGetAll, neighborhoodGetAll } from '@src/store/slice/lookupSlice';
import { addressSave } from '@src/store/slice/addressSlice';

const DeliveryAddressAddForm = ({ modalVisible, handleModalVisible, handleSelectedAddress }) => {
  const { cityList, countyList, neighborhoodList } = useSelector((state) => state?.lookup);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  useEffect(() => {
    if (modalVisible && cityList.length === 0) {
      (async () => {
        await dispatch(cityGetAll());
      })();
    }
  }, [cityList.length, dispatch, modalVisible]);

  const cityChange = useCallback(
    async (cityId) => {
      try {
        await dispatch(countyGetAll(cityId));
      } catch (e) { }
    },
    [dispatch],
  );

  const countyChange = useCallback(
    async (countyId) => {
      try {
        await dispatch(neighborhoodGetAll(countyId));
      } catch (e) { }
    },
    [dispatch],
  );

  const handleClose = useCallback(() => {
    handleModalVisible(false);
    form.resetFields();
  }, [form, handleModalVisible]);

  const onSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const body = {
        ...values,
        receiverPhone: values?.receiverPhone
          .replace(/\)/g, '')
          .replace(/\(/g, '')
          .replace(/-/g, '')
          .replace(/ /g, ''),
      };

      const action = await dispatch(addressSave(body));
      if (addressSave.fulfilled.match(action)) {
        successDialog({
          title: <Text t="successfullySent" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: () => {
            handleSelectedAddress?.({
              addressValue: action?.payload?.data?.id,
              addressText: action?.payload?.data?.addressName,
            });
            form.resetFields();
          },
        });
      }
    } catch (e) { }
  }, [dispatch, form, handleSelectedAddress]);

  return (
    <CustomModal
      className="delivery-address-add delivery-address-add-modal"
      width={810}
      maskClosable={false}
      footer={
        <div className={'footer-content'}>
          <div className={'cancel-ok-buttons-content'}>
            <span onClick={handleClose} className={'cancel-button'}>
              <Text t="giveUp" />
            </span>

            <CustomButton
              id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-ok-button"
              type="primary"
              onClick={onSave}
              className={'ok-button'}
            >
              <Text t="saveBtn" />
            </CustomButton>
          </div>
        </div>
      }
      title={<Text t="newDeliveryAddress" />}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <CustomForm
        name="deliveryAddressAddForm"
        initialValues={{}}
        autoComplete="off"
        layout={'vertical'}
        form={form}
      >
        <div className="row ">
          <div className="col-md-12">
            <CustomFormItem
              label={<Text t="addressName" />}
              name="addressName"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomInput
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-addressName-input"
                placeholder={useText('giveYourAddressANameEGOurcompanyheadquarters')}
              />
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="recipientName" />}
              name="receiverName"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomTextInput
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-receiverName-textInput"
                placeholder={useText('enterName')}
              />
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="recipientSurname" />}
              name="receiverSurName"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomTextInput
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-receiverSurName-textInput"
                placeholder={useText('enterSurname')}
              />
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="recipientPhone" />}
              name="receiverPhone"
              className={'delivery-address-add-form-item'}
              rules={[
                { required: true, message: <Text t="requiredField2" /> },
                { validator: formPhoneRegex, message: <Text t="enterValidPhoneNumber" /> },
              ]}
            >
              <CustomMaskInput
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-receiverPhone-maskInput"
                mask={'+\\90 (999) 999 99 99'}
              >
                <CustomInput
                  id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-receiverPhone-input"
                  placeholder={useText('enterMobilePhone')}
                />
              </CustomMaskInput>
            </CustomFormItem>
          </div>

          <div className="col-md-12">
            <CustomFormItem
              label={<Text t="address2" />}
              name="detail"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomInput
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-detail-input"
                placeholder={useText('enterOpenAddress')}
              />
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="city" />}
              name="cityId"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-cityId-select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  turkishToLower(option.children).includes(turkishToLower(input))
                }
                placeholder={useText('choose')}
                onChange={cityChange}
              >
                {cityList.map((item) => {
                  return (
                    <Option
                      id={`ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-cityId-${item?.name}-option`}
                      key={item?.id}
                      value={item?.id}
                    >
                      {item?.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="district" />}
              name="countyId"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-countyId-select"
                placeholder={useText('choose')}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  turkishToLower(option.children).includes(turkishToLower(input))
                }
                disabled={countyList.length === 0}
                onChange={countyChange}
              >
                {countyList.map((item) => {
                  return (
                    <Option
                      id={`ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-countyId-${item?.name}-option`}
                      key={item?.id}
                      value={item?.id}
                    >
                      {item?.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="neighborhood" />}
              name="neighborhoodId"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-neighborhoodId-select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  turkishToLower(option.children).includes(turkishToLower(input))
                }
                placeholder={useText('choose')}
                disabled={neighborhoodList.length === 0}
              >
                {neighborhoodList.map((item) => {
                  return (
                    <Option
                      id={`ticketRestaurant-cardOrders-cardInformation-deliveryAddressAddForm-neighborhoodId-${item?.name}-option`}
                      key={item?.id}
                      value={item?.id}
                    >
                      {item?.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </CustomFormItem>
          </div>
        </div>
      </CustomForm>
    </CustomModal>
  );
};

export default DeliveryAddressAddForm;
